import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    LinearProgress,
    TextField,
    Typography
} from '@mui/material';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import ReactCrop, {Crop} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {useApiService, useStatsLogger} from "../../../../context/ApiServiceContext";
import {AxiosError, AxiosResponse} from "axios";
import {toast} from "react-toastify";
import {PresignedUploadResponse, uploadFileToS3} from "../../../../utils/uploadUtils";
import {useMobile} from "../../../../context/MobileContext";
import {useTranslation} from "react-i18next";
import Asset from "../../../../models/Asset";
import {AiPromptVariant} from "../../../../models/Template";
import {StatEventType} from "../../../../models/StatEvent";
import {useTemplate} from "../../../../context/TemplateContext";
import {pollBgRemovalUntilComplete} from "../../../../services/jobService";
import AiMagicStage from "../../AiMagic/AiMagicStage";
import logger from "../../../../utils/logger";
import {capturePosterBlob} from "./videoPoster";
import {computeCenteredCrop, MAX_UPLOAD_HEIGHT} from "../../FillMode/cropUtils";

/** The whole image, in ReactCrop's percent units. Free-form upload starts here:
 *  a replaced image is contained inside the field box at render time, so
 *  pre-cropping it to the frame ratio would discard pixels for nothing. */
const WHOLE_IMAGE_CROP: Crop = {unit: '%', x: 0, y: 0, width: 100, height: 100};

interface ImagePreUploadDialogProps {
    open: boolean;
    selectedFile: File | null;
    onClose: () => void;
    onUploadSuccess: (asset: Asset) => void;
    folderId: string;
    dimensions: { width: number, height: number };
    /** The template input this upload targets — needed to start an AI edit job. */
    inputKey?: string;
    /** This field offers AI editing AND the team may use it. When true the dialog
     *  stays open after a successful upload and shows the AI magic stage. */
    aiEditable?: boolean;
    /** Named AI style options (names only) for the AI magic picker. */
    aiPromptVariants?: AiPromptVariant[];
    /** Field removes background → the AI stage skips its result-crop step. */
    aiRemoveBackground?: boolean;
    /** Called instead of onUploadSuccess when the user accepts the AI result —
     *  the desktop grid also SELECTS it (the plain upload only lands in the grid). */
    onAiAccept?: (asset: Asset) => void;
    /** Selects an asset on the board (the grid's click-to-apply path). Used to
     *  apply the PLAIN photo first on desktop before the AI stage, so "Keep
     *  original" leaves the plain image applied. */
    onSelectAsset?: (asset: Asset) => void;
}

const ImagePreUploadDialog: React.FC<ImagePreUploadDialogProps> = ({
                                                                       open,
                                                                       selectedFile,
                                                                       onClose,
                                                                       folderId,
                                                                       dimensions,
                                                                       onUploadSuccess,
                                                                       inputKey,
                                                                       aiEditable,
                                                                       aiPromptVariants,
                                                                       aiRemoveBackground,
                                                                       onAiAccept,
                                                                       onSelectAsset
                                                                   }) => {
    const {isMobile} = useMobile();
    const {template} = useTemplate();
    const [fileName, setFileName] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const apiService = useApiService();
    const statsLogger = useStatsLogger();
    const [uploadProgress, setUploadProgress] = useState(0);
    const [crop, setCrop] = useState<Crop>(WHOLE_IMAGE_CROP);
    const [percentCrop, setPercentCrop] = useState<Crop>(WHOLE_IMAGE_CROP);
    // FREE-FORM by default: a user image is contained inside the field box now
    // (nothing is cut), so pre-cropping it to the frame ratio would throw away
    // pixels the render would have shown. Cropping stays available — it is just
    // no longer the default, and no longer aspect-locked.
    const [keepAspectRatio, setKeepAspectRatio] = useState(false);
    // Whether a frame aspect is even known. Gates the "keep aspect ratio"
    // checkbox — and therefore the blur-background option, which needs a target
    // ratio and is disabled while the crop is unlocked. Keyed on the DIMENSIONS
    // rather than on `allowImageTransform` so a field that has a size always
    // offers both, and a size-less field (whose aspect would be NaN) offers
    // neither instead of silently locking to NaN.
    const hasFrameAspect = (dimensions?.width ?? 0) > 0 && (dimensions?.height ?? 0) > 0;
    const [bluredBackground, setBluredBackground] = useState(false);
    const [removeBackground, setRemoveBackground] = useState(false);
    const [bgRemovalProgress, setBgRemovalProgress] = useState<number | null>(null);
    const [bgRemovalMessage, setBgRemovalMessage] = useState<string>('');
    const cancelledRef = React.useRef(false);
    // Client-captured video poster (JPEG). Uploaded to S3 during registration so
    // the backend never has to run ffmpeg on the request path (see A1).
    const posterBlobRef = useRef<Blob | null>(null);
    const {t} = useTranslation();
    const fakeProgressRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const startFakeProgress = useCallback(() => {
        const stages = [
            { at: 0, msg: t("editor.uploadImageDialog.bgStages.analyzing") },
            { at: 8, msg: t("editor.uploadImageDialog.bgStages.detecting") },
            { at: 20, msg: t("editor.uploadImageDialog.bgStages.removing") },
            { at: 45, msg: t("editor.uploadImageDialog.bgStages.refining") },
            { at: 65, msg: t("editor.uploadImageDialog.bgStages.finishing") },
        ];
        let fakeVal = 0;
        setBgRemovalProgress(0);
        setBgRemovalMessage(stages[0].msg);

        fakeProgressRef.current = setInterval(() => {
            fakeVal += 0.5 + Math.random() * 0.8;
            if (fakeVal > 85) fakeVal = 85; // never reach 100 on fake
            setBgRemovalProgress(Math.round(fakeVal));
            const stage = [...stages].reverse().find(s => fakeVal >= s.at);
            if (stage) setBgRemovalMessage(stage.msg);
        }, 800);
    }, [t]);

    const stopFakeProgress = useCallback(() => {
        if (fakeProgressRef.current) {
            clearInterval(fakeProgressRef.current);
            fakeProgressRef.current = null;
        }
    }, []);

    // Store a resized preview src here
    const [previewSrc, setPreviewSrc] = useState<string>('');

    // Set once the plain upload succeeded on an AI-editable field: the dialog
    // body swaps to the AI magic stage instead of closing.
    const [aiSource, setAiSource] = useState<Asset | null>(null);

    // Video-specific state
    const [isVideo, setIsVideo] = useState(false);
    const [videoMetadata, setVideoMetadata] = useState<{width: number, height: number, duration: number} | null>(null);
    const [validationError, setValidationError] = useState<string | null>(null);

    // Constants for video validation
    const MAX_VIDEO_SIZE_MB = 300;
    const MAX_VIDEO_DURATION_SEC = 180;
    const MAX_VIDEO_SIZE_BYTES = MAX_VIDEO_SIZE_MB * 1024 * 1024;

    // Helper function to extract video metadata
    const extractVideoMetadata = (file: File): Promise<{width: number, height: number, duration: number}> => {
        return new Promise((resolve, reject) => {
            const video = document.createElement('video');
            video.preload = 'metadata';

            video.onloadedmetadata = () => {
                window.URL.revokeObjectURL(video.src);
                resolve({
                    width: video.videoWidth,
                    height: video.videoHeight,
                    duration: video.duration,
                });
            };

            video.onerror = () => {
                window.URL.revokeObjectURL(video.src);
                reject(new Error('Failed to load video metadata'));
            };

            video.src = URL.createObjectURL(file);
        });
    };

    // Helper function to resize an image Blob/File before showing or uploading
    // maxHeight param adjusts the final height; width is scaled accordingly.
    const resizeImage = (file: File, maxHeight: number): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = e => {
                const img = new Image();
                img.onload = () => {
                    // Calculate new dimensions
                    const ratio = img.width / img.height;
                    let newWidth = img.width;
                    let newHeight = img.height;
                    if (newHeight > maxHeight) {
                        newHeight = maxHeight;
                        newWidth = newHeight * ratio;
                    }
                    // Create an offscreen canvas and draw the resized image
                    const canvas = document.createElement('canvas');
                    canvas.width = newWidth;
                    canvas.height = newHeight;
                    const ctx = canvas.getContext('2d');
                    ctx!.drawImage(img, 0, 0, newWidth, newHeight);
                    // Convert canvas to base64 for preview
                    const dataUrl = canvas.toDataURL(file.type || 'image/png');
                    resolve(dataUrl);
                };
                img.onerror = reject;
                img.src = e.target?.result as string;
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };

    // Cancel polling on unmount
    useEffect(() => {
        return () => { cancelledRef.current = true; };
    }, []);

    useEffect(() => {
        if (selectedFile) {
            const initialName = selectedFile.name.replace(/\.[^/.]+$/, '');
            setFileName(initialName);
            setValidationError(null); // Reset validation error
            setRemoveBackground(false);
            setBgRemovalProgress(null);
            setUploadProgress(0);
            cancelledRef.current = false;

            const fileIsVideo = selectedFile.type.startsWith('video/');
            setIsVideo(fileIsVideo);

            if (fileIsVideo) {
                // Check file size first
                if (selectedFile.size > MAX_VIDEO_SIZE_BYTES) {
                    const fileSizeMB = (selectedFile.size / (1024 * 1024)).toFixed(2);
                    setValidationError(t('editor.uploadImageDialog.validation.videoTooLarge', {
                        size: fileSizeMB,
                        max: MAX_VIDEO_SIZE_MB
                    }));
                    return;
                }

                // Video handling: Extract metadata
                extractVideoMetadata(selectedFile)
                    .then(metadata => {
                        logger.debug('Video metadata:', metadata);

                        // Validate video duration
                        if (metadata.duration > MAX_VIDEO_DURATION_SEC) {
                            setValidationError(t('editor.uploadImageDialog.validation.videoTooLong', {
                                duration: metadata.duration.toFixed(1),
                                max: MAX_VIDEO_DURATION_SEC
                            }));
                            setVideoMetadata(metadata);
                            return;
                        }

                        setVideoMetadata(metadata);
                        // Create video preview
                        const videoUrl = URL.createObjectURL(selectedFile);
                        setPreviewSrc(videoUrl);
                    })
                    .catch(err => {
                        console.error('Error extracting video metadata:', err);
                        setValidationError(t('editor.uploadImageDialog.validation.videoMetadataFailed'));
                    });
            } else {
                // Image handling: build the downscaled preview. The CROP is
                // initialised by its own effect below.
                resizeImage(selectedFile, 640)
                    .then(resizedPreview => {
                        setPreviewSrc(resizedPreview);
                    })
                    .catch(err => {
                        console.error('Error resizing preview:', err);
                    });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFile]);

    /**
     * Crop initialisation — deliberately its own effect.
     *
     * It is the only thing here that depends on `keepAspectRatio`, and folding
     * it into the effect above would mean every toggle of the checkbox also
     * wiped the typed file name, the remove-background choice and the upload
     * progress, and re-ran resizeImage.
     *
     * Unlocked (the default): the WHOLE image — a replaced image is contained
     * inside its field box at render time, so a frame-aspect crop would discard
     * pixels the render would have kept. Locked: the largest centered crop at
     * the frame's aspect, built from a FRESH whole-image base (a spread of the
     * current free-form crop carried the user's x/y offset into the locked box
     * and pushed the selection off the image).
     */
    useEffect(() => {
        if (!selectedFile || selectedFile.type.startsWith('video/')) return;
        if (!keepAspectRatio) {
            setCrop(WHOLE_IMAGE_CROP);
            setPercentCrop(WHOLE_IMAGE_CROP);
            return;
        }
        let cancelled = false;
        const reader = new FileReader();
        reader.onload = e => {
            const img = new Image();
            img.onload = () => {
                if (cancelled) return;
                const centered = computeCenteredCrop(
                    img.width, img.height, dimensions?.width, dimensions?.height) as Crop;
                setCrop(centered);
                setPercentCrop(centered);
            };
            img.src = (e.target as any).result as string;
        };
        reader.readAsDataURL(selectedFile);
        return () => { cancelled = true; };
    }, [selectedFile, dimensions?.width, dimensions?.height, keepAspectRatio]);

    const cropImage = (): Promise<Blob> => {
        return new Promise((resolve, reject) => {
            if (percentCrop.width && percentCrop.height && selectedFile) {
                const image = new Image();

                image.src = URL.createObjectURL(selectedFile);
                image.onload = () => {
                    // If blur background is requested, we skip the "crop" step and just pass the entire image
                    if (bluredBackground) {
                        const canvas = document.createElement('canvas');
                        canvas.width = image.width;
                        canvas.height = image.height;

                        // Draw the image onto the canvas
                        const ctx = canvas.getContext('2d');
                        ctx!.drawImage(image, 0, 0);
                        // Downscale only if the source is taller than the cap.
                        const finalBlob = resizeCanvasToMaxHeight(canvas, selectedFile.type || 'image/png', MAX_UPLOAD_HEIGHT);
                        resolve(finalBlob);
                    } else {

                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');
                        // Set canvas size to the dimensions of the cropped image
                        canvas.width = (percentCrop.width / 100) * image.width;
                        canvas.height = (percentCrop.height / 100) * image.height;

                        // Draw the cropped portion of the image onto the canvas
                        ctx!.drawImage(
                            image,
                            (percentCrop.x / 100) * image.width,
                            (percentCrop.y / 100) * image.height,
                            (percentCrop.width / 100) * image.width,
                            (percentCrop.height / 100) * image.height,
                            0,
                            0,
                            canvas.width,
                            canvas.height
                        );
                        // Same cap for the cropped canvas.
                        const finalBlob = resizeCanvasToMaxHeight(canvas, selectedFile.type || 'image/png', MAX_UPLOAD_HEIGHT);
                        resolve(finalBlob);
                    }
                };
                image.onerror = reject;
            }
        });
    };

    // Helper to downscale a canvas to a given max height
    const resizeCanvasToMaxHeight = (canvas: HTMLCanvasElement, mimeType: string, maxHeight: number): Blob => {
        const originalWidth = canvas.width;
        const originalHeight = canvas.height;
        if (originalHeight <= maxHeight) {
            // No scaling needed, return the blob directly
            return canvasToBlob(canvas, mimeType);
        }
        const ratio = originalWidth / originalHeight;
        const newHeight = maxHeight;
        const newWidth = newHeight * ratio;
        const newCanvas = document.createElement('canvas');
        newCanvas.width = newWidth;
        newCanvas.height = newHeight;
        const newCtx = newCanvas.getContext('2d');
        newCtx!.drawImage(canvas, 0, 0, newWidth, newHeight);
        return canvasToBlob(newCanvas, mimeType);
    };

    // Helper to create a Blob from a canvas
    const canvasToBlob = (canvas: HTMLCanvasElement, mimeType: string): Blob => {
        const base64 = canvas.toDataURL(mimeType);
        const byteString = atob(base64.split(',')[1]);
        const arrayBuffer = new Uint8Array(byteString.length);
        for (let i = 0; i < byteString.length; i++) {
            arrayBuffer[i] = byteString.charCodeAt(i);
        }
        return new Blob([arrayBuffer], {type: mimeType});
    };

    const onFileNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFileName(event.target.value);
    };


    // Upload a poster JPEG via the SAME presigned flow used for the video and
    // return its S3 URL. Any failure resolves to null (upload proceeds without it).
    const uploadPosterToS3 = async (blob: Blob): Promise<string | null> => {
        try {
            const response = await apiService.api.post<PresignedUploadResponse>(
                `/folder/${folderId}/asset/upload`,
                {},
                {headers: {'Content-Type': 'image/jpeg'}}
            );
            const {presignedPost, url} = response.data;
            await new Promise<void>((resolve, reject) => {
                uploadFileToS3(
                    presignedPost,
                    new File([blob], 'poster.jpg', {type: 'image/jpeg'}),
                    () => {},
                    () => resolve(),
                    (error) => reject(new Error(error))
                );
            });
            return url;
        } catch (err) {
            console.warn('[VIDEO] Poster upload failed, proceeding without thumbnail', err);
            return null;
        }
    };

    const handleUpload = async () => {
        if (!selectedFile || !folderId) return;

        // Additional safety check for validation errors
        if (validationError) {
            toast.error(validationError);
            return;
        }

        setIsLoading(true);

        try {
            let file: File;
            let mimeType: string;

            if (isVideo) {
                // For videos, upload the original file without processing
                file = selectedFile;
                mimeType = selectedFile.type;
                // Capture a poster client-side (best-effort) so the backend never
                // has to run ffmpeg. Null on any failure/timeout — upload proceeds.
                posterBlobRef.current = await capturePosterBlob(selectedFile);
            } else {
                // For images, get cropped + resized blob
                const blob = await cropImage();
                mimeType = bluredBackground ? 'image/png' : selectedFile.type || 'image/png';
                file = new File([blob], fileName, {type: mimeType});
            }

            logEvent(StatEventType.ASSET_UPLOAD_START);
            apiService.api.post(`/folder/${folderId}/asset/upload`, {}, {
                headers: {
                    'Content-Type': mimeType
                }
            }).then((response: AxiosResponse<PresignedUploadResponse>) => {
                const {presignedPost, url: assetUrl} = response.data;
                uploadFileToS3(
                    presignedPost,
                    file,
                    setUploadProgress,
                    (url) => {
                        logger.debug('Upload successful', assetUrl);
                        updateTemplateWithNewAsset(assetUrl);
                        logEvent(StatEventType.ASSET_UPLOADED);
                    },
                    (error) => {
                        console.error('Upload failed', error);
                        setIsLoading(false);
                        logEvent(StatEventType.ASSET_UPLOAD_FAILED);
                    }
                );
            }).catch((error: AxiosError<{ message?: string }>) => {
                console.error('Upload failed', error);
                setIsLoading(false);
                logEvent(StatEventType.ASSET_UPLOAD_FAILED);

                // Check if it's a permission error
                const errorMessage = error.response?.data?.message || '';
                if (errorMessage.includes('video support') || errorMessage.includes('not allowed')) {
                    toast.error(errorMessage);
                } else {
                    toast.error(t('editor.upload.failed'));
                }
            });
        } catch (err) {
            console.error('Error processing asset:', err);
            setIsLoading(false);
        }
    };

    const updateTemplateWithNewAsset = async (url: string) => {

        const payload: {
            name: string;
            url: string;
            type: string;
            width: number;
            height: number;
            duration?: number;
            thumbnailUrl?: string;
            removeBackground?: boolean;
            crop?: {
                x: number;
                y: number;
                dx: number;
                dy: number;
            }
        } = {
            'name': fileName,
            url,
            type: selectedFile!.type,
            width: isVideo && videoMetadata ? videoMetadata.width : dimensions.width,
            height: isVideo && videoMetadata ? videoMetadata.height : dimensions.height
        };

        // Add video-specific fields
        if (isVideo && videoMetadata) {
            payload.duration = videoMetadata.duration;

            // Upload the client-captured poster (if any) and register it as the
            // thumbnail. Failure here is non-fatal — the backend falls back to
            // using the video URL as the thumbnail (see A1).
            if (posterBlobRef.current) {
                const thumbnailUrl = await uploadPosterToS3(posterBlobRef.current);
                if (thumbnailUrl) {
                    payload.thumbnailUrl = thumbnailUrl;
                }
                posterBlobRef.current = null;
            }
        }

        // Add image-specific fields
        if (!isVideo && bluredBackground) {
            payload.crop = {
                x: percentCrop.x,
                y: percentCrop.y,
                dx: percentCrop.width,
                dy: percentCrop.height,
            };
        }

        // Add background removal flag
        if (!isVideo && removeBackground) {
            payload.removeBackground = true;
        }

        const response = await apiService.api.put<Asset | { jobId: string }>(`/folder/${folderId}/asset/upload`, payload);

        // If bg-removal was requested, poll for completion
        if (removeBackground && 'jobId' in response.data) {
            const jobId = response.data.jobId;
            cancelledRef.current = false;
            startFakeProgress();
            try {
                const result = await pollBgRemovalUntilComplete(
                    apiService.api,
                    jobId,
                    () => {}
                );
                if (cancelledRef.current) return;
                stopFakeProgress();
                setBgRemovalProgress(100);
                setBgRemovalMessage(t("editor.uploadImageDialog.bgStages.done"));
                if (result.asset) {
                    setIsLoading(false);
                    onUploadSuccess(result.asset);
                    if (offerAiStage(result.asset)) return;
                    onClose();
                } else {
                    throw new Error('Background removal completed but no asset returned');
                }
            } catch (err: any) {
                if (cancelledRef.current) return;
                stopFakeProgress();
                console.error('Background removal failed:', err);
                toast.error(err.message || 'Background removal failed');
                setIsLoading(false);
                setBgRemovalProgress(null);
                setBgRemovalMessage('');
            }
            return;
        }

        setIsLoading(false);
        const asset = response.data as Asset;
        onUploadSuccess(asset);
        if (offerAiStage(asset)) return;
        onClose();
    };

    // The plain image is applied FIRST; only then do we offer the AI edit.
    // Returns true when the dialog should stay open for the AI stage.
    const offerAiStage = (asset: Asset): boolean => {
        if (!aiEditable || isVideo || !inputKey || !template) return false;
        // Desktop: the plain upload only lands in the grid, so SELECT it now —
        // otherwise "Keep original" would leave the PREVIOUS image applied.
        onSelectAsset?.(asset);
        setAiSource(asset);
        return true;
    };

    useEffect(() => {
        return () => { stopFakeProgress(); };
    }, [stopFakeProgress]);

    const handleClose = () => {
        if (!isLoading) {
            stopFakeProgress();
            cancelledRef.current = true;
            onClose();
        }
    };

    const logEvent = (eventName: StatEventType.ASSET_UPLOADED | StatEventType.ASSET_UPLOAD_START | StatEventType.ASSET_UPLOAD_FAILED) => {
        if (template) {
            statsLogger.logEvent({
                eventType: eventName,
                templateId: template.id,
                templateName: template.name,
            });
        }
    }

    // Stage 2 — the uploaded image is already applied; offer the AI edit.
    if (aiSource) {
        return (
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle>{t("aiEdit.title")}</DialogTitle>
                <DialogContent sx={{pb: 3}}>
                    <AiMagicStage
                        api={apiService.api}
                        templateId={template?.id ?? ''}
                        inputKey={inputKey ?? ''}
                        sourceAsset={aiSource}
                        aiPromptVariants={aiPromptVariants}
                        frameSize={dimensions}
                        folderId={folderId}
                        removeBackground={aiRemoveBackground}
                        onAccept={(asset) => {
                            (onAiAccept ?? onUploadSuccess)(asset);
                            onClose();
                        }}
                        onKeepOriginal={onClose}
                    />
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={handleClose}>
            <DialogTitle>
                <div>{t("editor.uploadImageDialog.title")}</div>
                {isMobile && isLoading && <>
                    <LinearProgress
                        variant={(bgRemovalProgress !== null ? bgRemovalProgress : uploadProgress) < 100 ? 'determinate' : 'indeterminate'}
                        value={bgRemovalProgress !== null ? bgRemovalProgress : uploadProgress}
                    />
                    {bgRemovalMessage && <div style={{fontSize: 13, color: '#888', marginTop: 4, textAlign: 'center'}}>{bgRemovalMessage}</div>}
                </>}
            </DialogTitle>
            <DialogContent>
                <Grid container columnSpacing={2}>
                    <Grid item xs={12} md={5}>
                        {selectedFile && (
                            <Grid container columnSpacing={2}>
                                <Grid item xs={12}>
                                    {/* Free-form crop by default (Part A): say so,
                                        so nobody hunts for the frame we no longer
                                        force them into. */}
                                    {!isVideo && (
                                        <Typography variant="caption" data-testid="upload-crop-hint"
                                                    sx={{display: 'block', color: '#888', mb: 1}}>
                                            {t('editor.uploadImageDialog.cropHint')}
                                        </Typography>
                                    )}
                                    {isVideo ? (
                                        // Video preview
                                        <video
                                            src={previewSrc || ''}
                                            controls
                                            style={{width: '100%', height: 'auto', maxHeight: '640px'}}
                                        />
                                    ) : (
                                        // Image cropper
                                        <ReactCrop
                                            crop={crop}
                                            disabled={isLoading}
                                            keepSelection={true}
                                            onComplete={(_, percentageCrop) => {
                                                setPercentCrop(percentageCrop);
                                            }}
                                            onChange={(newCrop: Crop) => setCrop(newCrop)}
                                            // Keep aspect ratio unless blur is requested
                                            aspect={!bluredBackground && keepAspectRatio ? dimensions.width / dimensions.height : undefined}
                                        >
                                            {/*
                                              Use the previewSrc which is already resized to max 640 height
                                              This way we also reduce memory usage for large images.
                                            */}
                                            <img
                                                src={previewSrc || ''}
                                                alt="Preview"
                                                style={{width: '100%', height: 'auto', maxHeight: '640px'}}
                                            />
                                        </ReactCrop>
                                    )}
                                </Grid>
                                {!isVideo && hasFrameAspect && <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={keepAspectRatio}
                                                onChange={(e) => {
                                                    setKeepAspectRatio(e.target.checked)
                                                    if (!e.target.checked) {
                                                        setBluredBackground(false)
                                                    }
                                                }}
                                                disabled={isLoading}
                                            />
                                        }
                                        label={t("editor.uploadImageDialog.keepAspectRatio")}
                                    />
                                </Grid>}
                                {/* Blur fills the LETTERBOX around an aspect-locked
                                    image, so without a frame aspect there is
                                    nothing for it to fill and the box would be
                                    permanently disabled — hide it instead of
                                    showing a control that can never be used. */}
                                {!isVideo && hasFrameAspect && <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={bluredBackground}
                                                onChange={(e) => {
                                                    setBluredBackground(e.target.checked);
                                                    if (e.target.checked) setRemoveBackground(false);
                                                }}
                                                disabled={isLoading || !keepAspectRatio}
                                            />
                                        }
                                        label={t("editor.uploadImageDialog.addBlurBackground")}
                                    />
                                </Grid>}
                                {!isVideo && <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={removeBackground}
                                                onChange={(e) => {
                                                    setRemoveBackground(e.target.checked);
                                                    if (e.target.checked) setBluredBackground(false);
                                                }}
                                                disabled={isLoading}
                                            />
                                        }
                                        label={t("editor.uploadImageDialog.removeBackground")}
                                    />
                                </Grid>}
                                {isVideo && videoMetadata && <Grid item xs={12}>
                                    <div style={{padding: '8px', background: '#f5f5f5', borderRadius: '4px', color: '#333'}}>
                                        <div>משך: {videoMetadata.duration.toFixed(2)} שניות</div>
                                        <div>גודל: {videoMetadata.width}x{videoMetadata.height}</div>
                                        <div>גודל קובץ: {(selectedFile!.size / (1024 * 1024)).toFixed(2)} MB</div>
                                    </div>
                                </Grid>}
                                {validationError && <Grid item xs={12}>
                                    <div style={{padding: '12px', background: '#ffebee', borderRadius: '4px', color: '#c62828', border: '1px solid #ef5350'}}>
                                        <strong style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                                            <WarningAmberRounded fontSize="small"/>
                                            {t("editor.uploadImageDialog.validation.title")}
                                        </strong>
                                        <div style={{marginTop: '4px'}}>{validationError}</div>
                                    </div>
                                </Grid>}
                            </Grid>
                        )}
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <TextField
                            fullWidth
                            label={t("editor.uploadImageDialog.fileNamePlaceholder")}
                            value={fileName}
                            onChange={onFileNameChange}
                            disabled={isLoading}
                            sx={{marginTop: 1}}
                        />
                    </Grid>
                </Grid>
                {!isMobile && isLoading && <>
                    <LinearProgress
                        variant={(bgRemovalProgress !== null ? bgRemovalProgress : uploadProgress) < 100 ? 'determinate' : 'indeterminate'}
                        value={bgRemovalProgress !== null ? bgRemovalProgress : uploadProgress}
                    />
                    {bgRemovalMessage && <div style={{fontSize: 13, color: '#888', marginTop: 4, textAlign: 'center'}}>{bgRemovalMessage}</div>}
                </>}
            </DialogContent>
            <DialogActions sx={{justifyContent: 'space-between'}}>
                {/* The AI step is a SECOND dialog that opens after this one is
                    confirmed, so nothing here hints it exists — and users read
                    that absence as "this field has no AI". */}
                {aiEditable && !isVideo ? (
                    <Typography variant="caption" sx={{color: '#7CE0A6', px: 1}} data-testid="upload-ai-hint">
                        {t("aiEdit.afterUploadHint")}
                    </Typography>
                ) : <span/>}
                <Box>
                <Button onClick={handleUpload} disabled={isLoading || !!validationError}>
                    {t("editor.uploadImageDialog.upload")}
                </Button>
                <Button onClick={handleClose} disabled={isLoading}>
                    {t("editor.uploadImageDialog.cancel")}
                </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default ImagePreUploadDialog;
